import React from "react"
import { Tag, Tooltip } from "@blueprintjs/core"
import { ACTIONS, RouteStop } from "../../reducers/routes2gSlice"
import { isMobile } from "../../utils/detectDevice"
import { Location } from "../../reducers/locationsSlice"
import {
  DisplayedStopDistance,
  formatStopDistance,
  getDisplayedStopDistance,
} from "@/lib/stopDistance"
import { formatTime } from "@/components/maps/mapUtils"
import { shownTrafficDelay } from "@/components/maps/routeTraffic"
import type { Leg } from "@/components/maps/RouteView"

type ReadOnlyRouteStopProps = {
  stop: RouteStop
  displayedDistance: DisplayedStopDistance | undefined
}

const ReadOnlyRouteStop = ({
  stop,
  displayedDistance,
}: ReadOnlyRouteStopProps) => {
  const isPassthrough = stop.action === "passthrough"

  const getCoordinates = (location: Location) => {
    if (location.lat && location.lon) {
      const lat = parseFloat(location.lat).toFixed(5)
      const lon = parseFloat(location.lon).toFixed(5)
      return `${lat}, ${lon}`
    }

    return undefined
  }

  const getLocationAddress = (location: Location) => {
    if (location.formattedAddress?.trim()) {
      return location.formattedAddress.trim()
    }

    if (location.countryCode && location.locality) {
      return `${location.countryCode}, ${location.postalCode ? location.postalCode : ""} ${location.locality}`
    }

    return getCoordinates(location)
  }

  const getActionIcon = () => {
    const action =
      ACTIONS.find((action) => action.value.toLowerCase() === stop.action) ||
      ACTIONS.find((action) => action.value === "other")!

    return <action.icon size={16} />
  }

  const formatAddress = () => {
    const location = stop.location
    const companyName = location.companyName?.trim()
    const address = getLocationAddress(location)

    if (companyName) {
      return {
        primary: companyName,
        secondary:
          address && address !== companyName && address.trim().length > 0
            ? address
            : undefined,
      }
    }

    return {
      primary: address || "Unknown location",
      secondary: undefined,
    }
  }

  const locationLabel = formatAddress()

  const renderDistance = () => {
    if (!stop.proximity_status) return null

    if (stop.proximity_status === "arrived") return "Arrived"

    if (stop.proximity_status === "arriving") {
      return (
        stop.proximity_status.charAt(0).toUpperCase() +
        stop.proximity_status.slice(1)
      )
    }

    // Tested on the distance from the vehicle, not on the leg, so a zero-length
    // leg between two stops still renders.
    if (!stop.remaining_distance) return null
    if (!displayedDistance) return null

    return formatStopDistance(displayedDistance, "pl-PL")
  }

  return (
    <tr>
      <td className="w-px">
        <div className="flex min-h-[30px] min-w-[30px] items-center px-[10px] py-[5px]">
          {getActionIcon()}
        </div>
      </td>
      <td className="w-full">
        <Tooltip
          disabled={isMobile}
          content={
            <div className="max-w-xs">
              <div>{locationLabel.primary}</div>
              {locationLabel.secondary && (
                <div className="text-xs text-gray-500">
                  {locationLabel.secondary}
                </div>
              )}
            </div>
          }
          hoverOpenDelay={500}
        >
          <div className="min-w-0 px-[10px]">
            <div className="truncate">{locationLabel.primary}</div>
            {locationLabel.secondary && (
              <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                {locationLabel.secondary}
              </div>
            )}
          </div>
        </Tooltip>
      </td>
      <td className="whitespace-nowrap text-right">
        {!isPassthrough && renderDistance() && (
          <Tag round className="dark:bg-gray-700 dark:text-gray-200">
            {renderDistance()}
          </Tag>
        )}
      </td>
    </tr>
  )
}

type ReadOnlyRouteStopListProps = {
  routeStops: RouteStop[] | undefined
  /** From the shared route link, never from the feature flag service. */
  showLegDistance?: boolean
  /**
   * When the API returns no per-stop distances but does return a total route
   * distance (routes[0].distance), pass it here and it will be shown as a
   * footer below the stop list instead of next to individual stops.
   */
  totalRouteDistanceMeters?: number
  legs?: Leg[]
}

const ReadOnlyRouteStopList = ({
  routeStops,
  showLegDistance = false,
  totalRouteDistanceMeters,
  legs,
}: ReadOnlyRouteStopListProps) => {
  if (!routeStops || routeStops.length === 0) {
    return <div className="p-2 text-center text-gray-500">No stops</div>
  }

  const { seconds: trafficDelay } = shownTrafficDelay(legs, { colors: false })

  return (
    <div className="w-full">
      <table className={`w-full [&_td]:p-[3px] ${isMobile ? "text-sm" : ""}`}>
        <tbody className="dark:text-gray-200">
          {routeStops.map((stop, index) => (
            <ReadOnlyRouteStop
              stop={stop}
              displayedDistance={getDisplayedStopDistance(
                routeStops,
                index,
                showLegDistance,
              )}
              key={index}
            />
          ))}
        </tbody>
      </table>
      {totalRouteDistanceMeters !== undefined && (
        <div className="mt-2 border-t pt-2 text-right text-sm text-gray-500 dark:text-gray-400">
          {formatStopDistance(
            { distanceMeters: totalRouteDistanceMeters, isApproximate: false },
            "pl-PL",
          )}{" "}
          total
        </div>
      )}
      {trafficDelay !== null && (
        <div className="mt-2 text-right text-sm text-amber-600 dark:text-amber-400">
          +{formatTime(trafficDelay)} traffic
        </div>
      )}
    </div>
  )
}

export default ReadOnlyRouteStopList
